import { ComponentService } from '../Services/Axios/Api';
import { generateDynamicCSS } from '../ComponentsMap';

export const DEFAULT_ARCHIVE_STYLE = {
    backgroundColor: '#ffffff',
};

export const DEFAULT_ARCHIVE_TITLE_STYLE = {
    color: '#212529',
};

export const DEFAULT_ARCHIVE_FILE_STYLE = {
    backgroundColor: '#ffffff',
    titleColor: '#212529',
    borderColor: '#e9ecef',
};

const createDefaultFile = (id, order = 1) => ({
    id,
    order,
    title: 'Document title',
    fileUrl: '',
    fileName: '',
});

export const sortFilesByOrder = (files) =>
    [...(files || [])].sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));

export const normalizeFileOrders = (files) =>
    (files || []).map((file, index) => ({ ...file, order: index + 1 }));

export const getFileDisplayName = (file) => {
    if (file?.fileName?.trim()) return file.fileName.trim();
    if (!file?.fileUrl) return 'download';
    try {
        const part = file.fileUrl.split('/').pop()?.split('?')[0];
        return part ? decodeURIComponent(part) : 'download';
    } catch {
        return 'download';
    }
};

class Archive {
    data = {};

    constructor() {
        this.data = {
            id: '',
            ComponentName: 'Archive',
            sectionTitle: '',
            files: [createDefaultFile(1)],
            style: {
                Archive: { ...DEFAULT_ARCHIVE_STYLE },
                ArchiveTitle: { ...DEFAULT_ARCHIVE_TITLE_STYLE },
                ArchiveFile: { ...DEFAULT_ARCHIVE_FILE_STYLE },
            },
        };
    }

    getId() { return this.data.id; }
    setId(id) { this.data.id = id; }

    getComponentName() { return this.data.ComponentName; }
    setComponentName(name) { this.data.ComponentName = name; }

    getSectionTitle() { return this.data.sectionTitle || ''; }
    setSectionTitle(title) { this.data.sectionTitle = title; }

    getFiles() { return sortFilesByOrder(this.data.files || []); }
    setFiles(files) { this.data.files = normalizeFileOrders(files); }

    addFile(file) {
        const existing = this.getFiles();
        const nextOrder = existing.length
            ? Math.max(...existing.map((f) => Number(f.order) || 0)) + 1
            : 1;
        const newFile = file || createDefaultFile(Date.now(), nextOrder);
        this.setFiles([...existing, newFile]);
    }

    removeFile(id) {
        this.setFiles(this.getFiles().filter((f) => f.id !== id));
    }

    getStyle() { return this.data.style; }
    setStyle(style) { this.data.style = style; }

    getData() { return this.data; }
    setData(data) {
        this.data = data;
        if (!this.data.files?.length) {
            this.data.files = [createDefaultFile(1)];
        }
        this.data.style = {
            Archive: { ...DEFAULT_ARCHIVE_STYLE, ...this.data.style?.Archive },
            ArchiveTitle: { ...DEFAULT_ARCHIVE_TITLE_STYLE, ...this.data.style?.ArchiveTitle },
            ArchiveFile: { ...DEFAULT_ARCHIVE_FILE_STYLE, ...this.data.style?.ArchiveFile },
        };
        const withDefaults = (this.data.files || []).map((file, index) => ({
            ...file,
            order: file.order ?? index + 1,
        }));
        this.data.files = normalizeFileOrders(sortFilesByOrder(withDefaults));
    }

    renderStyles() {
        const sectionId = this.getId();
        if (!sectionId) return;

        const section = this.data.style?.Archive || {};
        const title = this.data.style?.ArchiveTitle || {};
        const fileStyle = this.data.style?.ArchiveFile || {};

        generateDynamicCSS(`Archive-${sectionId}`, [
            { backgroundColor: section.backgroundColor || '#ffffff' },
        ]);
        generateDynamicCSS(`ArchiveTitle-${sectionId}`, [
            { color: title.color || '#212529' },
        ]);
        const accentColor = fileStyle.titleColor || '#212529';
        const rowBorderColor = fileStyle.borderColor || '#e9ecef';
        generateDynamicCSS(`ArchiveFile-${sectionId}`, [
            {
                backgroundColor: fileStyle.backgroundColor || '#ffffff',
                borderColor: rowBorderColor,
                '--archive-file-accent': accentColor,
                '--archive-file-border': rowBorderColor,
            },
        ]);
        generateDynamicCSS(`ArchiveFileTitle-${sectionId}`, [
            { color: fileStyle.titleColor || '#212529' },
        ]);
    }

    async load(id) {
        try {
            if (id) {
                const response = await ComponentService.getComponentById(id);
                const data = response.data ? JSON.parse(response.data) : {};
                this.setData(data);
                this.setId(response.id);
            } else {
                const response = await ComponentService.getComponent(this.data.ComponentName);
                const data = response[0]?.data ? JSON.parse(response[0].data) : {};
                this.setData(data);
                this.setId(response[0]?.id);
            }
            this.renderStyles();
            return this.data;
        } catch (error) {
            throw new Error(error.response?.data || 'Failed to load archive data');
        }
    }

    async save() {
        try {
            const response = await ComponentService.SaveData(this.getId(), this.getData());
            this.renderStyles();
            return response;
        } catch (error) {
            throw new Error(error.response?.data || 'Failed to save archive data');
        }
    }
}

export const createArchiveInstance = () => new Archive();
export { createDefaultFile };
